























































































import { PhotosService } from '~/services/photos/PhotosService'
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  toRefs
} from '@nuxtjs/composition-api'
import { useClassifiedForm } from '~/compositions/form'
import { formFieldProps } from '~/constants/form'
import { faInfo } from '@fortawesome/free-solid-svg-icons'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_FORM_NS,
  FormState
} from '~/store/modules/shared/classifieds/form/state'
import { useDep } from '~/compositions/dependency-container'
import {
  DEFAULT_MAX_NATIVE_CLASSIFIED_PHOTOS,
  DEFAULT_MAX_PANORAMIC_CLASSIFIED_PHOTOS
} from '~/constants/classified/photos'
import { Photo } from '~/models/photos/types'
import { UploaderFile } from '~/models/photos/uploader'
import { useId } from '~/compositions/id'
import { useConcurrentQueue } from '~/compositions/queue'
import FieldError from '~/components/car/classifieds/form/fieldsets/fields/FieldError.vue'
import { ci360Logo } from '~/icons/source/solid/360logo'
import { useFile } from '~/compositions/files/file'
import CFileUploader from '~/components/shared/configurable/file/CFileUploader.vue'
import { useI18n } from '~/compositions/i18n'
import { debounce } from '~/utils/function'
import { DEBOUNCE_SLOW } from '~/constants/debounce'

export default defineComponent({
  components: {
    FieldError,
    CFileUploader
  },
  props: formFieldProps,
  setup(props) {
    const { name, specs } = toRefs(props)
    const { state, getters } = useNamespacedStore<FormState>(CLASSIFIED_FORM_NS)
    const { t } = useI18n()

    const isVehiclesXymaOrPartsForm = computed(() =>
      getters('isVehiclesXymaOrPartsForm')
    )

    const classifiedId = computed(() => state.classifiedId)
    const editMode = computed(() => state.editMode)
    const vehicleModelImage = computed(() => getters('vehicleModelImage'))
    const isRentalsForm = computed(() => getters('isRentalsForm'))

    const nativePhotos = ref<UploaderFile[]>([])
    const panoramicPhotos = ref<UploaderFile[]>([])

    const loading = ref(true)
    const maxNativeImages = ref(DEFAULT_MAX_NATIVE_CLASSIFIED_PHOTOS)
    const maxPanoramicImages = ref(DEFAULT_MAX_PANORAMIC_CLASSIFIED_PHOTOS)

    const { error, valueChanged } = useClassifiedForm(props)
    const { resizeAndReadImage, fileToBase64 } = useFile()
    const photosService = useDep(PhotosService)
    photosService.setUrl(
      editMode.value
        ? `/api/classifieds/${classifiedId.value}/photos/`
        : '/api/classifieds/photos/'
    )

    const { uuid } = useId()
    const { createQueue } = useConcurrentQueue()
    const nativeImagesQueue = createQueue({ concurrency: 1 })
    const panoramicImagesQueue = createQueue({ concurrency: 1 })

    async function loadPhotos() {
      const { limits, photos } = await photosService.getPhotosInfo()
      maxNativeImages.value =
        limits?.native ||
        specs.value?.max_photos ||
        DEFAULT_MAX_NATIVE_CLASSIFIED_PHOTOS
      maxPanoramicImages.value =
        limits?.panoramic || DEFAULT_MAX_PANORAMIC_CLASSIFIED_PHOTOS

      nativePhotos.value = (photos?.native || []).map(mapPhotoToUploaderFile)
      panoramicPhotos.value = (photos?.panoramic || []).map(
        mapPhotoToUploaderFile
      )

      loading.value = false
    }

    function mapPhotoToUploaderFile(photo: Photo): UploaderFile {
      return {
        name: photo.name,
        uploading: false,
        thumb: photo.sizes.p,
        attached: true,
        id: uuid(),
        title: photo.title
      }
    }

    async function handleNativeImagesAdd(files: File[]) {
      for (const file of files) {
        const { thumb, photo } = await resizeAndReadImage(file)
        const id = uuid()
        nativePhotos.value.push({
          thumb,
          name: '',
          uploading: true,
          attached: false,
          id,
          progress: 0
        })
        const currentFile = nativePhotos.value.find(
          (f: UploaderFile) => f.id === id
        )!

        nativeImagesQueue.push(async () => {
          try {
            const { file: uploadedFile } = await photosService.uploadPhoto(
              photo,
              isVehiclesXymaOrPartsForm.value,
              progressEvt =>
                (currentFile.progress = Math.round(
                  (progressEvt.loaded * 100) / progressEvt.total!
                ))
            )
            currentFile.name = uploadedFile.name
            currentFile.attached = true
            currentFile.uploading = false
            updateValue()
          } catch (error) {
            currentFile.error =
              (error as any)?.response?.data?.message ||
              t('something went wrong')
          }
        })
      }
    }
    function handleNativeImagesDelete({
      name: imageName,
      id: imageId
    }: {
      name: string
      id: string
    }) {
      if (editMode.value) {
        photosService.deletePhoto(imageName)
      }
      nativePhotos.value = nativePhotos.value.filter(
        (p: UploaderFile) => p.id !== imageId
      )
      updateValue()
    }

    function handleNativeImagesChange(photos: UploaderFile[]) {
      if (editMode.value) {
        photosService.updatePhotos(photos.map(f => f.name))
      }
      nativePhotos.value = photos
      updateValue()
    }

    async function handlePanoramicImagesAdd(files: File[]) {
      for (const file of files) {
        const id = uuid()
        panoramicPhotos.value.push({
          thumb: await fileToBase64(file),
          name: '',
          uploading: true,
          attached: false,
          id,
          progress: 0,
          error: undefined
        })
        const currentFile = panoramicPhotos.value.find(
          (f: UploaderFile) => f.id === id
        )!

        panoramicImagesQueue.push(async () => {
          try {
            const { file: uploadedFile } = await photosService.uploadPhoto(
              file,
              isVehiclesXymaOrPartsForm.value,
              progressEvt =>
                (currentFile.progress = Math.round(
                  (progressEvt.loaded * 100) / progressEvt.total!
                )),
              true
            )
            currentFile.name = uploadedFile.name
            currentFile.attached = true
            currentFile.uploading = false
            // if the photo is determined as non-panoramic from the backend, move it to the native photos
            if (!uploadedFile.isPanoramic) {
              // using filter instead of pop here in case the photos were moved while uploading
              panoramicPhotos.value = panoramicPhotos.value.filter(
                p => p.id !== currentFile.id
              )
              nativePhotos.value.push(currentFile)
            }
            updateValue()
          } catch (error) {
            currentFile.error =
              (error as any)?.response?.data?.message ||
              t('something went wrong')
          }
        })
      }
    }

    function handlePanoramicImagesDelete({
      name,
      id
    }: {
      name: string
      id: string
    }) {
      if (editMode.value) {
        photosService.deletePhoto(name, true)
      }
      panoramicPhotos.value = panoramicPhotos.value.filter(
        (p: UploaderFile) => p.id !== id
      )
      updateValue()
    }
    function handlePanoramicImagesChange(photos: UploaderFile[]) {
      if (editMode.value) {
        photosService.updatePhotos(
          photos.map(f => f.name),
          true
        )
      }
      panoramicPhotos.value = photos
      updateValue()
    }

    const handlePanoramicImagesTitleChange = debounce(
      () => updateValue(),
      DEBOUNCE_SLOW
    )

    onMounted(async () => {
      await loadPhotos()
    })

    const isPlotForm = computed(() => getters('isPlotForm'))

    const icons = {
      info: faInfo,
      logo360: ci360Logo
    }

    function updateValue() {
      valueChanged(name.value, {
        native: {
          values: nativePhotos.value.map(p => ({
            name: p.name
          }))
        },
        panoramic: {
          values: panoramicPhotos.value.map(p => ({
            name: p.name,
            title: p.title
          }))
        }
      })
    }

    return {
      classifiedId,
      editMode,
      error,
      icons,
      vehicleModelImage,
      maxNativeImages,
      maxPanoramicImages,
      loading,
      nativePhotos,
      panoramicPhotos,
      isPlotForm,
      isRentalsForm,
      handleNativeImagesDelete,
      handleNativeImagesChange,
      handleNativeImagesAdd,
      handlePanoramicImagesAdd,
      handlePanoramicImagesDelete,
      handlePanoramicImagesChange,
      handlePanoramicImagesTitleChange
    }
  }
})
